import { useCallback, useEffect, useRef, useState } from "react";

export type PortalMutationState =
  | { status: "idle" }
  | { status: "pending"; action: string }
  | { status: "success"; action: string }
  | { status: "error"; action: string; error: unknown };

export function usePortalMutation() {
  const [state, setState] = useState<PortalMutationState>({ status: "idle" });
  const controllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  useEffect(
    () => () => {
      requestVersionRef.current += 1;
      controllerRef.current?.abort();
    },
    [],
  );

  const run = useCallback(
    async <TResult>(
      action: string,
      request: (signal: AbortSignal) => Promise<TResult>,
    ): Promise<TResult | undefined> => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      const requestVersion = requestVersionRef.current + 1;
      requestVersionRef.current = requestVersion;
      controllerRef.current = controller;
      setState({ status: "pending", action });

      try {
        const result = await request(controller.signal);
        if (
          controller.signal.aborted ||
          requestVersionRef.current !== requestVersion
        ) {
          return undefined;
        }
        controllerRef.current = null;
        setState({ status: "success", action });
        return result;
      } catch (error) {
        if (
          controller.signal.aborted ||
          requestVersionRef.current !== requestVersion
        ) {
          return undefined;
        }
        controllerRef.current = null;
        setState({ status: "error", action, error });
        return undefined;
      }
    },
    [],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);
  return { state, run, reset };
}
